import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { api, setCsrf, startSessionKeepalive, User } from './api';

type Auth = {
  user: User | null;
  loading: boolean;
  notice: string;
  login: (identity: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const C = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');

  const expireSession = useCallback(() => {
    setNotice('Сессия истекла. Войдите снова для приёма звонков.');
    setUser(null);
    setCsrf('');
  }, []);

  const refresh = useCallback(async () => {
    const session = await api<{ user: User; csrfToken: string }>('/auth/session');
    setUser(session.user);
    setCsrf(session.csrfToken);
  }, []);

  useEffect(() => {
    void refresh()
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, [refresh]);

  useEffect(() => {
    if (!user) return;
    return startSessionKeepalive({ refresh, expire: expireSession });
  }, [expireSession, refresh, user?.id]);

  const login = async (identity: string, password: string) => {
    const session = await api<{ user: User; csrfToken: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identity, password }),
    });
    setUser(session.user);
    setCsrf(session.csrfToken);
    setNotice('');
  };

  const logout = async () => {
    await api('/auth/logout', { method: 'POST' });
    setUser(null);
    setCsrf('');
    setNotice('');
  };

  return <C.Provider value={{ user, loading, notice, login, logout, refresh }}>{children}</C.Provider>;
}

export function useAuth() {
  const value = useContext(C);
  if (!value) throw new Error('AuthProvider missing');
  return value;
}
